import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import DownloadIcon from '@mui/icons-material/Download';

const VoteStatistics = ({ setLoading, setError, setSuccess }) => {
  const theme = useTheme();
  const [elections, setElections] = useState([]);
  const [activeElection, setActiveElection] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [localLoading, setLocalLoading] = useState(true);
  const [allVotingData, setAllVotingData] = useState([]);

  // Fetch all elections when component mounts
  useEffect(() => {
    const fetchElections = async () => {
      try {
        setLocalLoading(true);
        const response = await axios.get('/api/elections/');
        setElections(response.data);
        
        // Don't auto-select, let user choose
        setLocalLoading(false);
      } catch (error) {
        console.error('Error fetching elections:', error);
        setError('Failed to fetch elections. Please try again.');
        setLocalLoading(false);
      }
    };
    
    fetchElections();
  }, [setError]);

  // Fetch statistics for an election
  const fetchStatistics = async (electionId) => {
    try {
      setLocalLoading(true);
      const response = await axios.get(`/api/votes/statistics/${electionId}`);
      setStatistics(response.data);
      
      // Prepare data for CSV export
      const allData = [];
      Object.entries(response.data).forEach(([businessUnit, candidates]) => {
        candidates.forEach(candidate => {
          allData.push({
            businessUnit,
            candidateName: candidate.candidate_name,
            voteCount: candidate.vote_count,
            isWinner: candidates.indexOf(candidate) === 0 ? 'Yes' : 'No'
          });
        });
      });
      setAllVotingData(allData);
      
      setLocalLoading(false);
    } catch (error) {
      console.error('Error fetching vote statistics:', error);
      setError('Failed to fetch vote statistics. Please try again.');
      setLocalLoading(false);
    }
  };
  
  // Function to convert data to CSV and trigger download
  const downloadCSV = () => {
    try {
      // Safety checks
      if (allVotingData.length === 0) {
        setError('No voting data available to download');
        return;
      }
      
      // Create CSV header
      const csvHeader = ['Business Unit', 'Candidate Name', 'Vote Count', 'Is Winner'];
      
      // Create CSV content
      const csvContent = [
        csvHeader.join(','),
        ...allVotingData.map(item => [
          `"${item.businessUnit || ''}"`,
          `"${item.candidateName || ''}"`,
          item.voteCount || 0,
          item.isWinner || 'No'
        ].join(','))
      ].join('\n');
      
      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `voting_statistics_${timestamp}.csv`;
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // For browsers that support the download attribute
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // For IE
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // For modern browsers
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
      }
      
      setSuccess('Statistics downloaded successfully!');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setError('Failed to download statistics. Please try again.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Vote Statistics
        </Typography>
        
        {/* Election Selection */}
        <FormControl variant="outlined" sx={{ minWidth: 200, mr: 2 }}>
          <InputLabel>Select Election</InputLabel>
          <Select
            value={activeElection?.id || ''}
            onChange={(e) => {
              const selectedElection = elections.find(el => el.id === e.target.value);
              setActiveElection(selectedElection);
              if (selectedElection) {
                fetchStatistics(selectedElection.id);
              }
            }}
            label="Select Election"
          >
            {elections.map(election => (
              <MenuItem key={election.id} value={election.id}>
                {election.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {activeElection && Object.keys(statistics).length > 0 ? (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadCSV}
            size="small"
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
          >
            Download CSV
          </Button>
        ) : null}
      </Box>
      
      {localLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : !activeElection ? (
        <Alert severity="info">No elections found. Please create an election first.</Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            Results for: {activeElection.title}
          </Typography>
          
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Showing winners for each business unit
          </Typography>
          
          {Object.keys(statistics).length === 0 ? (
            <Alert severity="info">No votes have been cast in this election yet.</Alert>
          ) : (
            <Grid container spacing={3}>
              {Object.entries(statistics).map(([businessUnit, candidates]) => (
                <Grid item xs={12} md={6} lg={4} key={businessUnit}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 2,
                      position: 'relative',
                      overflow: 'visible',
                      background: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    {candidates.length > 0 && (
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: -15,
                          right: 20,
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: theme.palette.secondary.main,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          zIndex: 1
                        }}
                      >
                        <EmojiEventsIcon sx={{ color: 'white' }} />
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        {businessUnit}
                      </Typography>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      {candidates.length > 0 ? (
                        <>
                          {/* Winner */}
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                              Winner
                            </Typography>
                            <Avatar 
                              sx={{ 
                                bgcolor: 'primary.main',
                                width: 64,
                                height: 64,
                                mx: 'auto',
                                mb: 2
                              }}
                            >
                              {candidates[0].candidate_name.charAt(0)}
                            </Avatar>
                            <Typography variant="body1" fontWeight="medium" gutterBottom>
                              {candidates[0].candidate_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                              <HowToVoteIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.7 }} />
                              <Typography variant="body2" color="text.secondary">
                                {candidates[0].vote_count} votes
                              </Typography>
                            </Box>
                            
                            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                              Total candidates: {candidates.length}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No votes have been cast in this business unit.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default VoteStatistics;
